/**
 * Empty / error / auth-expired states. Mobile layout is handled by the surface
 * (Drawer), not here.
 */

import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 1.5,
      p: 3,
      color: 'text.secondary',
    }}
  >
    {children}
  </Box>
);

export const EmptyState: React.FC<{
  suggestions?: string[];
  onPick?: (s: string) => void;
}> = ({ suggestions = [], onPick }) => (
  <Centered>
    <AutoAwesomeIcon sx={{ fontSize: 36, color: 'primary.main' }} />
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
      How can I help with this course?
    </Typography>
    <Typography variant="body2">
      Ask a question or request changes — I can propose edits for you to review.
    </Typography>
    {suggestions.length > 0 && (
      <Stack spacing={1} sx={{ mt: 1, width: '100%', maxWidth: 320 }}>
        {suggestions.map((s) => (
          <Button key={s} variant="outlined" size="small" onClick={() => onPick?.(s)}>
            {s}
          </Button>
        ))}
      </Stack>
    )}
  </Centered>
);

export const ErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({
  message,
  onRetry,
}) => (
  <Centered>
    <ErrorOutlineIcon sx={{ fontSize: 36, color: 'error.main' }} />
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
      Something went wrong
    </Typography>
    <Typography variant="body2">{message}</Typography>
    {onRetry && (
      <Button variant="contained" size="small" onClick={onRetry}>
        Try again
      </Button>
    )}
  </Centered>
);

export const AuthExpiredState: React.FC<{ onReauth: () => void }> = ({ onReauth }) => (
  <Centered>
    <LockOutlinedIcon sx={{ fontSize: 36, color: 'warning.main' }} />
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
      Your session expired
    </Typography>
    <Typography variant="body2">Sign in again to keep using the AI.</Typography>
    <Button variant="contained" size="small" onClick={onReauth}>
      Sign in
    </Button>
  </Centered>
);
